import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {useCallback, useMemo, useState} from 'react'
import type {Assignee} from './api/assignee'
import {useUpdateIssueAssigneesBulkMutation} from './mutations/use-update-issue-assignees-bulk-mutation'
import {AssigneeRepositoryPicker, type AssigneePickerProps} from './AssigneePicker'
import type {AssigneePickerBaseAssignee} from '@github-ui/item-picker-assignee'

export type BulkIssueAssigneePickerProps = Omit<AssigneePickerProps, 'id' | 'assignees' | 'onSelectionChange'> & {
  assigneesAppliedToAll: AssigneePickerBaseAssignee[]
  assigneesAppliedToSome: AssigneePickerBaseAssignee[]
  existingIssueAssignees: Map<string, string[]>
  connectionIds: {[key: string]: string[]}
  issueNumbers: number[]
  repository?: {
    databaseId: number | null
    isPrivate: boolean
    isArchived: boolean
  }
  /** Optional externally managed ref to the anchor button for focus return after Copilot modal closes. */
  externalReturnFocusRef?: React.RefObject<HTMLElement | null>
} & SharedBulkActionsItemPickerProps &
  Parameters<typeof useUpdateIssueAssigneesBulkMutation>[0]

export function BulkIssueAssigneePicker({
  onError,
  onCompleted,
  onMutate,
  issuesToActOn,
  assigneesAppliedToAll,
  assigneesAppliedToSome,
  existingIssueAssignees,
  connectionIds,
  issueNumbers,
  repository,
  externalReturnFocusRef,
  ...rest
}: BulkIssueAssigneePickerProps) {
  const {mutate: bulkUpdateMutate} = useUpdateIssueAssigneesBulkMutation({onMutate})
  const [copilotModalOpen, setCopilotModalOpen] = useState(false)
  const [pickerInstanceKey, setPickerInstanceKey] = useState(0)

  const assigneesIncludeCopilot = useMemo(() => {
    return assigneesAppliedToAll.some(assignee => assignee.isCopilot)
  }, [assigneesAppliedToAll])

  const canUseCopilotAssignment = !assigneesIncludeCopilot

  const issueNumbersWithoutCopilot = useMemo(() => {
    const copilotId = [...assigneesAppliedToAll, ...assigneesAppliedToSome].find(a => a.isCopilot)?.id

    if (!copilotId) return [...issueNumbers]

    return issuesToActOn.reduce((acc: number[], issueId, idx) => {
      const assignees = existingIssueAssignees.get(issueId) || []
      const hasCopilot = assignees.some(id => id === copilotId)
      const numberAtIndex = issueNumbers[idx]
      if (!hasCopilot && typeof numberAtIndex === 'number') acc.push(numberAtIndex)
      return acc
    }, [])
  }, [assigneesAppliedToAll, assigneesAppliedToSome, existingIssueAssignees, issueNumbers, issuesToActOn])

  const executeBulkAssigneeMutation = useCallback(
    (assigneesToProcess: Assignee[]) => {
      const assigneesToAdd = assigneesToProcess.filter(a => !assigneesAppliedToAll.some(aa => aa.id === a.id))
      const assigneesToRemove = assigneesAppliedToAll.filter(a => !assigneesToProcess.some(aa => aa.id === a.id))

      if (!assigneesToAdd.length && !assigneesToRemove.length) {
        return
      }

      const ids = [...issuesToActOn]

      bulkUpdateMutate(
        {
          ids,
          applyAssigneeIds: assigneesToAdd.map(a => a.id),
          removeAssigneeIds: assigneesToRemove.map(a => a.id),
          assignees: assigneesToProcess,
        },
        {
          onSuccess: response => onCompleted?.(response.data.updateIssuesBulk?.jobId ?? undefined),
          onError,
        },
      )
    },
    [bulkUpdateMutate, issuesToActOn, assigneesAppliedToAll, onCompleted, onError],
  )

  const handleCopilotModalOpen = useCallback(() => {
    setCopilotModalOpen(true)
    /* Force a remount of the picker so it resets to initialSelectedItems.
      This is needed because when we do nothing with Copilot, it remains selected in the picker
      and the modal would reopen immediately when the picker opens */
    setPickerInstanceKey(prev => prev + 1)
  }, [])

  const onSelectionChange = useCallback(
    (selectedAssignees: Assignee[]) => {
      const copilotAssignee = selectedAssignees.find(assignee => assignee.isCopilot)
      const nonCopilotAssignees = selectedAssignees.filter(assignee => !assignee.isCopilot)

      if (copilotAssignee && canUseCopilotAssignment) {
        // open modal and process non-copilot changes in the background
        handleCopilotModalOpen()
        executeBulkAssigneeMutation(nonCopilotAssignees)
      } else {
        executeBulkAssigneeMutation(selectedAssignees)
      }
    },
    [executeBulkAssigneeMutation, canUseCopilotAssignment, handleCopilotModalOpen],
  )

  const showModal = canUseCopilotAssignment && copilotModalOpen

  const repositoryProps = useMemo(
    () => ({
      name: rest.repo,
      owner: {
        login: rest.owner,
      },
    }),
    [rest.repo, rest.owner],
  )

  return (
    <>
      <AssigneeRepositoryPicker
        key={`bulk-assignee-picker-${pickerInstanceKey}`}
        {...rest}
        onSelectionChange={onSelectionChange}
        assignees={assigneesAppliedToAll}
        assigneeTokens={[]}
        includeAssignableBots
        includeAuthorableBots={false}
        responsiveOnNarrow
      />
      {showModal && issueNumbersWithoutCopilot.length > 0 && (
        <CopilotAssignmentModal
          isOpen={copilotModalOpen}
          onClose={() => setCopilotModalOpen(false)}
          repository={repositoryProps}
          issueNumbers={issueNumbersWithoutCopilot}
          onAssignmentComplete={() => {
            setCopilotModalOpen(false)
            onCompleted?.()
          }}
          returnFocusRef={externalReturnFocusRef}
        />
      )}
    </>
  )
}
